import { MAX_TTS_TEXT_LENGTH } from './tts-constants';
import {
  unwrapApiData,
  getAiSettingsApiUrl,
  buildAiSettingsAuthHeaders,
} from './ai-settings-fetch';

import type { TtsSynthesizeRequest, TtsSynthesizeJsonPayload } from '../types/tts-synthesize.types';

const TTS_SYNTHESIZE_URL = 'api/ai-settings/tts/synthesize';

function base64ToBlob(base64: string, contentType: string): Blob {
  const normalized = base64.includes(',') ? (base64.split(',')[1] ?? base64) : base64;
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

/** Parse binary audio or JSON `{ data: { audio: "<base64>", contentType } }` from the API. */
export async function parseTtsSynthesizeResponse(response: Response): Promise<Blob> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const json = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      const payload = unwrapApiData<TtsSynthesizeJsonPayload>(json);
      throw new Error(
        payload.message ?? (json.message as string) ?? `TTS synthesis failed (${response.status})`
      );
    }

    const payload = unwrapApiData<TtsSynthesizeJsonPayload>(json);
    const audio = payload.audio ?? payload.audioBase64 ?? payload.content;
    if (typeof audio === 'string' && audio.length > 0) {
      const mime = payload.contentType ?? payload.mimeType ?? 'audio/mpeg';
      return base64ToBlob(audio, mime);
    }

    throw new Error(payload.message ?? 'TTS synthesis returned no audio data.');
  }

  if (!response.ok) {
    throw new Error(`TTS synthesis failed (${response.status})`);
  }

  return response.blob();
}

/**
 * Synthesize speech via tenant-configured TTS provider (Google / OpenAI) on the backend.
 */
export async function synthesizeTtsAudio(
  request: TtsSynthesizeRequest,
  signal?: AbortSignal
): Promise<Blob> {
  const response = await fetch(getAiSettingsApiUrl(TTS_SYNTHESIZE_URL), {
    method: 'POST',
    headers: buildAiSettingsAuthHeaders(true),
    body: JSON.stringify({
      ...request,
      format: request.format ?? 'mp3',
    }),
    signal,
  });

  return parseTtsSynthesizeResponse(response);
}

/** Map 2-letter UI lang prefix to BCP-47 for the synthesis API. */
export function resolveBcp47Language(langPrefix: string): string {
  const map: Record<string, string> = {
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    es: 'es-ES',
    it: 'it-IT',
    pt: 'pt-BR',
    zh: 'zh-CN',
    ja: 'ja-JP',
    ko: 'ko-KR',
    vi: 'vi-VN',
    hi: 'hi-IN',
    ar: 'ar-AE',
    ru: 'ru-RU',
    he: 'he-IL',
  };
  const prefix = langPrefix.toLowerCase().slice(0, 2);
  return map[prefix] || 'en-US';
}

/**
 * Synthesize full message text in a single backend request (valid MP3).
 * Backend should concatenate long text server-side when needed.
 */
export async function synthesizeTtsAudioForText(
  text: string,
  options: { language: string; voice?: string; format?: TtsSynthesizeRequest['format'] },
  signal?: AbortSignal
): Promise<Blob> {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('Cannot synthesize empty text.');
  }

  if (trimmed.length > MAX_TTS_TEXT_LENGTH) {
    throw new Error(
      `Message is too long for voice synthesis (max ${MAX_TTS_TEXT_LENGTH.toLocaleString()} characters). Shorten the text and try again.`
    );
  }

  return synthesizeTtsAudio(
    {
      text: trimmed,
      language: options.language,
      voice: options.voice,
      format: options.format,
    },
    signal
  );
}
